// frontend/src/pages/admin/BackupRestoreManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Tabs, Card, Button, Space, Typography, Alert, 
  List, Tag, Modal, Form, Select, Input, TimePicker,
  InputNumber, message, Divider, Table, Spin,
  Upload, Switch
} from 'antd';
import { 
  CloudDownloadOutlined, CloudUploadOutlined, 
  ReloadOutlined, DeleteOutlined, EditOutlined,
  HistoryOutlined, ScheduleOutlined,
  FolderOpenOutlined, PlusOutlined, UploadOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import './BackupRestore.css';

import moment from 'moment-timezone';

const { TabPane } = Tabs;
const { Text } = Typography;
const { Option } = Select;

const BackupRestoreManagement = () => {
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [restores, setRestores] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [activeTab, setActiveTab] = useState('manual');
  const [createScheduleModal, setCreateScheduleModal] = useState(false);
  const [editScheduleModal, setEditScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [selectedPath, setSelectedPath] = useState('');
  const [currentFieldName, setCurrentFieldName] = useState('');
  const [currentForm, setCurrentForm] = useState(null);
  const [directRestoreModal, setDirectRestoreModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content) => {
    notificationApi.error({ message: title, description: content });
  }, [notificationApi]);

  const showSuccess = useCallback((title) => {
    notificationApi.success({ message: title });
  }, [notificationApi]);

  // Fetch backups list
  const fetchBackups = useCallback(async () => {
    try {
      const response = await api.get('/api/backups');
      setBackups(response.data.data || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      showError('Failed to fetch backups', error.response?.data?.message || error.message);
    }
  }, [showError]);

  // Fetch restores list
  const fetchRestores = useCallback(async () => {
    try {
      const response = await api.get('/api/restores/history');
      setRestores(response.data.data || []);
    } catch (error) {
      console.error('Error fetching restores:', error);
      showError('Failed to fetch restores', error.response?.data?.message || error.message);
    }
  }, [showError]);

  // Fetch backup schedules
  const fetchSchedules = useCallback(async () => {
    try {
      const response = await api.get('/api/backup-schedules');
      setSchedules(response.data.data || []);
    } catch (error) {
      showError('Failed to fetch schedules', error.response?.data?.message || error.message);
    }
  }, [showError]);

  useEffect(() => {
    fetchBackups();
    fetchRestores();
    fetchSchedules();
  }, [fetchBackups, fetchRestores, fetchSchedules]);

  // Polling for status updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'history') {
        setHistoryLoading(true);
        Promise.all([fetchBackups(), fetchRestores()]).finally(() => setHistoryLoading(false));
      }
    }, 5000); // Poll every 5 seconds when on history tab
    return () => clearInterval(interval);
  }, [activeTab, fetchBackups, fetchRestores]);

  // Manual backup function (full only)
  const handleManualBackup = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/api/backups/create', values);
      const filename = response.data.filename || response.data.data?.filename || 'Backup';
      showSuccess(`Backup started: ${filename}`);
      fetchBackups();
    } catch (error) {
      showError('Backup failed', error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Schedule form submission (full only)
  const handleCreateSchedule = async (values) => {
    try {
      await api.post('/api/backup-schedules', values);
      showSuccess('Backup schedule created');
      setCreateScheduleModal(false);
      fetchSchedules();
    } catch (error) {
      showError('Failed to create schedule', error.response?.data?.message || error.message);
    }
  };

  // Update schedule function
  const handleUpdateSchedule = async (values) => {
    try {
      await api.put(`/api/backup-schedules/${editingSchedule.id}`, values);
      showSuccess('Backup schedule updated');
      setEditScheduleModal(false);
      setEditingSchedule(null);
      fetchSchedules();
    } catch (error) {
      showError('Failed to update schedule', error.response?.data?.message || error.message);
    }
  };

  // Edit schedule function
  const handleEditSchedule = async (schedule) => {
    setEditingSchedule(schedule);
    setEditScheduleModal(true);
  };

const handleDirectRestore = async () => {
  if (!uploadedFile) {
    console.error('[BackupRestoreManagement] No file selected for restore');
    showError('Restore failed', 'Please select a backup file first');
    return;
  }

  setLoading(true);
  try {
    console.log('[BackupRestoreManagement] Selected file:', uploadedFile.name);
    console.log('[BackupRestoreManagement] Preparing POST /api/restores/direct');
    
    const formData = new FormData();
    formData.append('file', uploadedFile);

    console.log('[BackupRestoreManagement] Sending POST /api/restores/direct with file:', uploadedFile.name);
    
    // Use the api instance directly - it should handle authentication automatically
    const response = await api.post('/api/restores/direct', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('[BackupRestoreManagement] POST /api/restores/direct response:', response.data);

    if (response.data.success) {
      console.log('[BackupRestoreManagement] Direct restore started successfully, restoreId:', response.data.data.restoreId);
      showSuccess('Direct restore process started');
      setDirectRestoreModal(false);
      setUploadedFile(null);
      
      setTimeout(() => {
        console.log('[BackupRestoreManagement] Refreshing restore history');
        fetchRestores();
      }, 1000);
    } else {
      console.error('[BackupRestoreManagement] Restore failed:', response.data.message);
      showError('Restore failed', response.data.message || 'Unknown error');
    }
  } catch (error) {
    console.error('[BackupRestoreManagement] Restore error:', error, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    showError('Restore failed', error.response?.data?.message || error.message);
  } finally {
    setLoading(false);
  }
};

  // Download backup function
  const handleDownload = async (backup) => {
    try {
      const response = await api.get(`/api/backups/${backup.id}/download`, {
        responseType: 'blob'
      });
      
      if (response.status !== 200) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const contentType = response.headers['content-type'];
      if (contentType.includes('application/json')) {
        // If server returned JSON (error), parse it
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            showError('Download Failed', errorData.message || 'Unknown error');
          } catch {
            showError('Download Failed', 'Failed to parse error response');
          }
        };
        reader.readAsText(response.data);
        return;
      }

      // Create a URL for the blob
      const url = window.URL.createObjectURL(response.data);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename || 'backup.sql');
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      showError('Download Failed', error.response?.data?.message || error.message || 'Failed to download backup');
    }
  };

  // Browse folder function
  const browseFolder = (fieldName, form) => {
    setCurrentFieldName(fieldName);
    setCurrentForm(form);
    setSelectedPath(form.getFieldValue(fieldName) || '');
    setFolderModalVisible(true);
  };

  const predefinedPaths = [
    { name: 'Local Backups', path: 'C:\\Backups' },
    { name: 'Database Backups', path: 'D:\\DatabaseBackups' },
    { name: 'Program Data', path: 'C:\\ProgramData\\HelpDesk\\Backups' },
    { name: 'Network Share', path: '\\\\NAS\\Backups\\HelpDesk' }
  ];

  return (
    <div className="backup-restore-management">
      <Card title="Backup & Restore Management" bordered={false}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            key="manual" 
            tab={
              <span>
                <CloudDownloadOutlined />
                Manual Backup
              </span>
            }
          >
            <ManualBackupTab 
              onBackup={handleManualBackup}
              loading={loading}
              onBrowseFolder={browseFolder}
            />
          </TabPane>

          <TabPane 
            key="scheduled" 
            tab={
              <span>
                <ScheduleOutlined />
                Scheduled Backups
              </span>
            }
          >
            <ScheduledBackupsTab
              schedules={schedules}
              onCreateSchedule={() => setCreateScheduleModal(true)}
              onEditSchedule={handleEditSchedule}
              onRefresh={fetchSchedules}
              onDeleteSchedule={async (id) => {
                try {
                  await api.delete(`/api/backup-schedules/${id}`);
                  showSuccess('Schedule deleted');
                  fetchSchedules();
                } catch (error) {
                  showError('Delete failed', error.response?.data?.message || error.message);
                }
              }}
            />
          </TabPane>

          <TabPane 
            key="direct" 
            tab={
              <span>
                <UploadOutlined />
                Direct Restore
              </span>
            }
          >
            <DirectRestoreTab
              onRestore={() => setDirectRestoreModal(true)}
            />
          </TabPane>

          <TabPane 
            key="history" 
            tab={
              <span>
                <HistoryOutlined />
                Backup & Restore History
              </span>
            }
          >
            <Spin spinning={historyLoading}>
              <BackupHistoryTab
                backups={backups}
                restores={restores}
                onDownload={handleDownload}
                onRefresh={() => {
                  setHistoryLoading(true);
                  Promise.all([fetchBackups(), fetchRestores()]).finally(() => setHistoryLoading(false));
                }}
                onDeleteBackup={async (id) => {
                  try {
                    await api.delete(`/api/backups/${id}`);
                    showSuccess('Backup deleted');
                    fetchBackups();
                  } catch (error) {
                    showError('Delete failed', error.response?.data?.message || error.message);
                  }
                }}
              />
            </Spin>
          </TabPane>
        </Tabs>
      </Card>

      {/* Folder Selection Modal */}
      <Modal
        title="Select Backup Location"
        open={folderModalVisible}
        onCancel={() => {
          setFolderModalVisible(false);
          if (!selectedPath && currentForm && currentFieldName) {
            currentForm.setFieldsValue({ [currentFieldName]: '' });
          }
        }}
        okButtonProps={{ style: { display: 'none' } }}
        width={600}
      >
        <div>
          <p>Choose from predefined locations or enter a custom path:</p>
          
          <Select
            style={{ width: '100%', marginBottom: '15px' }}
            placeholder="Select a predefined location"
            onChange={(value) => {
              setSelectedPath(value);
              if (currentForm && currentFieldName) {
                currentForm.setFieldsValue({ [currentFieldName]: value });
              }
              setFolderModalVisible(false);
            }}
          >
            {predefinedPaths.map((item, index) => (
              <Option key={index} value={item.path}>
                {item.name} - {item.path}
              </Option>
            ))}
          </Select>
          
          <Divider>Or Enter Custom Path</Divider>
          
          <Input
            placeholder="e.g., C:\\Backups\\HelpDesk"
            value={selectedPath}
            onChange={(e) => {
              setSelectedPath(e.target.value);
              if (currentForm && currentFieldName) {
                currentForm.setFieldsValue({ [currentFieldName]: e.target.value });
              }
            }}
            style={{ marginTop: '10px', width: '100%' }}
          />
          
          <Button 
            type="primary" 
            onClick={() => {
              if (selectedPath) {
                setFolderModalVisible(false);
              } else {
                message.warning('Please enter a custom path');
              }
            }}
            style={{ marginTop: '10px' }}
          >
            Use Custom Path
          </Button>
        </div>
      </Modal>

      {/* Create Schedule Modal */}
      <CreateScheduleModal
        open={createScheduleModal}
        onCancel={() => setCreateScheduleModal(false)}
        onSubmit={handleCreateSchedule}
        onBrowseFolder={browseFolder}
      />

      {/* Edit Schedule Modal */}
      <EditScheduleModal
        open={editScheduleModal}
        onCancel={() => {
          setEditScheduleModal(false);
          setEditingSchedule(null);
        }}
        onSubmit={handleUpdateSchedule}
        onBrowseFolder={browseFolder}
        schedule={editingSchedule}
      />

      {/* Direct Restore Modal */}
      <DirectRestoreModal
        open={directRestoreModal}
        onCancel={() => {
          setDirectRestoreModal(false);
          setUploadedFile(null);
        }}
        onRestore={handleDirectRestore}
        uploadedFile={uploadedFile}
        setUploadedFile={setUploadedFile}
        loading={loading}
      />
    </div>
  );
};

// Manual Backup Tab Component (full only)
const ManualBackupTab = ({ onBackup, loading, onBrowseFolder }) => {
  const [form] = Form.useForm();

  const handleSubmit = (values) => {
    onBackup(values);
  };

  return (
    <div className="manual-backup-tab">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Manual Backup"
          description="Create an immediate full backup of your database."
          type="info"
          showIcon
        />

        <Card title="Backup Options" size="small">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item name="label" label="Backup Label">
              <Input placeholder="e.g., Pre-maintenance backup" />
            </Form.Item>

            <Form.Item name="location" label="Backup Location" rules={[{ required: true, message: 'Please select a backup location' }]}>
              <Input.Group compact>
                <Form.Item name="location" noStyle rules={[{ required: true, message: 'Please select a backup location' }]}>
                  <Input 
                    placeholder="e.g., C:\\Backups\\HelpDesk"
                    style={{ width: 'calc(100% - 100px)' }} 
                  />
                </Form.Item>
                <Button 
                  icon={<FolderOpenOutlined />}
                  onClick={() => onBrowseFolder('location', form)}
                  style={{ width: 100 }}
                >
                  Browse
                </Button>
              </Input.Group>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<CloudDownloadOutlined />}
                loading={loading}
                size="large"
              >
                Start Backup
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

// Direct Restore Tab Component
const DirectRestoreTab = ({ onRestore }) => {
  return (
    <div className="direct-restore-tab">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Direct Restore"
          description="Upload a SQL backup file to restore your database directly."
          type="info"
          showIcon
        />

        <Card title="Restore Options" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>Select a SQL backup file to restore your database:</Text>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={onRestore}
              size="large"
            >
              Select Backup File
            </Button>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

// Scheduled Backups Tab Component (full only)
const ScheduledBackupsTab = ({ schedules, onCreateSchedule, onEditSchedule, onRefresh, onDeleteSchedule }) => (
  <div className="scheduled-backups-tab">
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>Automated backup schedules</Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={onRefresh}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateSchedule}>
            New Schedule
          </Button>
        </Space>
      </div>

      {schedules.length === 0 ? (
        <Card>
          <Text type="secondary">No backup schedules configured</Text>
        </Card>
      ) : (
        <List
          dataSource={schedules}
          renderItem={(schedule) => (
            <List.Item
              actions={[
                <Button 
                  type="link" 
                  icon={<EditOutlined />}
                  onClick={() => onEditSchedule(schedule)}
                >
                  Edit
                </Button>,
                <Button 
                  type="link" 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => onDeleteSchedule(schedule.id)}
                >
                  Delete
                </Button>
              ]}
            >
              <List.Item.Meta
                title={schedule.name}
                description={
                  <Space direction="vertical" size="small">
                    <Text>Type: {schedule.type}</Text>
                    <Text>Frequency: {schedule.frequency}</Text>
                    <Text>Time: {schedule.time}</Text>
                    {schedule.frequency === 'weekly' && <Text>Day: {getDayOfWeekName(schedule.day_of_week)}</Text>}
                    {schedule.frequency === 'monthly' && <Text>Day: {schedule.day_of_month}</Text>}
                    <Text>Location: {schedule.location}</Text>
                    <Text>Retention: {schedule.retention_days} days</Text>
                  </Space>
                }
              />
              <Tag color={schedule.enabled ? 'green' : 'red'}>
                {schedule.enabled ? 'Active' : 'Disabled'}
              </Tag>
            </List.Item>
          )}
        />
      )}
    </Space>
  </div>
);

// Backup & Restore History Tab Component with separate sections
const BackupHistoryTab = ({ backups, restores, onDownload, onRefresh, onDeleteBackup }) => {
  const backupColumns = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      width: '25%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: '15%',
      render: formatDateTime,
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: '15%',
      render: formatDateTime,
    },
    {
      title: 'Size',
      dataIndex: 'file_size',
      key: 'file_size',
      width: '10%',
      render: formatFileSize,
    },
    {
      title: 'Created By',
      key: 'created_by',
      width: '15%',
      render: (record) => `${record.created_by_first_name} ${record.created_by_last_name}`,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => {
        // Allow deletion if job is stuck (running for more than 30 minutes)
        const isStuck = record.status === 'running' && 
          new Date().getTime() - new Date(record.start_time).getTime() > 30 * 60 * 1000;
        
        return (
          <Space>
            <Button 
              type="link" 
              icon={<CloudDownloadOutlined />}
              onClick={() => onDownload(record)}
              disabled={record.status !== 'completed' || !record.file_size}
            >
              Download
            </Button>
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
              onClick={() => onDeleteBackup(record.id)}
              disabled={record.status === 'pending' || (record.status === 'running' && !isStuck)}
            >
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

const restoreColumns = [
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: '15%',
    render: (type, record) =>
      record.file_based_restore ? 'Direct file upload' : type?.toUpperCase(),
  },
  {
    title: 'File Name',
    dataIndex: 'original_filename',
    key: 'original_filename',
    width: '20%',
    render: (filename) => filename || 'N/A',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: '10%',
    render: (status) => (
      <Tag color={getStatusColor(status)}>
        {status?.toUpperCase()}
      </Tag>
    ),
  },
  {
    title: 'Error Message',
    dataIndex: 'error_message',
    key: 'error_message',
    width: '20%',
    render: (errorMessage) => errorMessage || 'N/A',
  },
  {
    title: 'Start Time',
    dataIndex: 'start_time',
    key: 'start_time',
    width: '15%',
    render: formatDateTime,
  },
  {
    title: 'End Time',
    dataIndex: 'end_time',
    key: 'end_time',
    width: '15%',
    render: formatDateTime,
  },
  {
    title: 'Performed By',
    key: 'performed_by',
    width: '25%',
    render: (record) => `${record.performed_by_first_name} ${record.performed_by_last_name}`,
  },
];

  return (
    <div className="backup-history-tab">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>Recent backups and restores</Text>
          <Button icon={<ReloadOutlined />} onClick={onRefresh}>
            Refresh
          </Button>
        </div>

        <Card title="Backups" style={{ marginBottom: 24 }}>
          {backups.length === 0 ? (
            <Text type="secondary">No backups found</Text>
          ) : (
            <Table 
              dataSource={backups.sort((a, b) => new Date(b.start_time) - new Date(a.start_time))} 
              columns={backupColumns} 
              rowKey="id" 
              pagination={{ pageSize: 10 }} 
              scroll={{ x: true }} 
            />
          )}
        </Card>

        <Card title="Restores">
          {restores.length === 0 ? (
            <Text type="secondary">No restores found</Text>
          ) : (
            <Table 
              dataSource={restores.sort((a, b) => new Date(b.start_time) - new Date(a.start_time))} 
              columns={restoreColumns} 
              rowKey="id" 
              pagination={{ pageSize: 10 }} 
              scroll={{ x: true }} 
            />
          )}
        </Card>
      </Space>
    </div>
  );
};

// Create Schedule Modal Component (full only)
const CreateScheduleModal = ({ open, onCancel, onSubmit, onBrowseFolder }) => {
  const [form] = Form.useForm();
  const [frequency, setFrequency] = useState('daily');
  const [loading, setLoading] = useState(false);

  const handleFrequencyChange = (value) => {
    setFrequency(value);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await onSubmit(values);
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Create Backup Schedule"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="name" label="Schedule Name" rules={[{ required: true, message: 'Please enter a schedule name' }]}>
          <Input placeholder="e.g., Nightly Full Backup" />
        </Form.Item>

        <Form.Item name="type" label="Backup Type" rules={[{ required: true, message: 'Please select a backup type' }]}>
          <Select defaultValue="full">
            <Option value="full">Full Backup</Option>
            <Option value="incremental">Incremental Backup</Option>
          </Select>
        </Form.Item>

        <Form.Item name="frequency" label="Frequency" rules={[{ required: true, message: 'Please select a frequency' }]}>
          <Select onChange={handleFrequencyChange}>
            <Option value="daily">Daily</Option>
            <Option value="weekly">Weekly</Option>
            <Option value="monthly">Monthly</Option>
          </Select>
        </Form.Item>

        {frequency === 'weekly' && (
          <Form.Item name="day_of_week" label="Day of Week" rules={[{ required: true, message: 'Please select a day of week' }]}>
            <Select>
              <Option value="1">Monday</Option>
              <Option value="2">Tuesday</Option>
              <Option value="3">Wednesday</Option>
              <Option value="4">Thursday</Option>
              <Option value="5">Friday</Option>
              <Option value="6">Saturday</Option>
              <Option value="0">Sunday</Option>
            </Select>
          </Form.Item>
        )}

        {frequency === 'monthly' && (
          <Form.Item name="day_of_month" label="Day of Month" rules={[{ required: true, message: 'Please select a day of month' }]}>
            <Select>
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <Option key={day} value={day}>{day}</Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="time" label="Backup Time" rules={[{ required: true, message: 'Please select a backup time' }]}>
          <TimePicker format="HH:mm" minuteStep={15} />
        </Form.Item>

        <Form.Item name="location" label="Backup Location" rules={[{ required: true, message: 'Please select a backup location' }]}>
          <Input.Group compact>
            <Form.Item name="location" noStyle rules={[{ required: true, message: 'Please select a backup location' }]}>
              <Input placeholder="e.g., C:\\Backups\\HelpDesk" />
            </Form.Item>
            <Button 
              icon={<FolderOpenOutlined />}
              onClick={() => onBrowseFolder('location', form)}
            >
              Browse
            </Button>
          </Input.Group>
        </Form.Item>

        <Form.Item name="retention_days" label="Retention Period (days)" rules={[{ required: true, message: 'Please enter retention days' }]}>
          <InputNumber min={1} max={365} defaultValue={30} />
        </Form.Item>

        <Form.Item name="enabled" label="Status" valuePropName="checked" initialValue={true}>
          <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" defaultChecked />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Edit Schedule Modal Component
const EditScheduleModal = ({ open, onCancel, onSubmit, onBrowseFolder, schedule }) => {
  const [form] = Form.useForm();
  const [frequency, setFrequency] = useState('daily');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (schedule) {
      form.setFieldsValue({
        name: schedule.name,
        type: schedule.type,
        frequency: schedule.frequency,
        day_of_week: schedule.day_of_week,
        day_of_month: schedule.day_of_month,
        time: schedule.time ? moment(schedule.time, 'HH:mm:ss') : null,
        location: schedule.location,
        retention_days: schedule.retention_days,
        enabled: schedule.enabled
      });
      setFrequency(schedule.frequency);
    }
  }, [schedule, form]);

  const handleFrequencyChange = (value) => {
    setFrequency(value);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await onSubmit(values);
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Backup Schedule"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="name" label="Schedule Name" rules={[{ required: true, message: 'Please enter a schedule name' }]}>
          <Input placeholder="e.g., Nightly Full Backup" />
        </Form.Item>

        <Form.Item name="type" label="Backup Type" rules={[{ required: true, message: 'Please select a backup type' }]}>
          <Select>
            <Option value="full">Full Backup</Option>
            <Option value="incremental">Incremental Backup</Option>
          </Select>
        </Form.Item>

        <Form.Item name="frequency" label="Frequency" rules={[{ required: true, message: 'Please select a frequency' }]}>
          <Select onChange={handleFrequencyChange}>
            <Option value="daily">Daily</Option>
            <Option value="weekly">Weekly</Option>
            <Option value="monthly">Monthly</Option>
          </Select>
        </Form.Item>

        {frequency === 'weekly' && (
          <Form.Item name="day_of_week" label="Day of Week" rules={[{ required: true, message: 'Please select a day of week' }]}>
            <Select>
              <Option value="1">Monday</Option>
              <Option value="2">Tuesday</Option>
              <Option value="3">Wednesday</Option>
              <Option value="4">Thursday</Option>
              <Option value="5">Friday</Option>
              <Option value="6">Saturday</Option>
              <Option value="0">Sunday</Option>
            </Select>
          </Form.Item>
        )}

        {frequency === 'monthly' && (
          <Form.Item name="day_of_month" label="Day of Month" rules={[{ required: true, message: 'Please select a day of month' }]}>
            <Select>
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <Option key={day} value={day}>{day}</Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="time" label="Backup Time" rules={[{ required: true, message: 'Please select a backup time' }]}>
          <TimePicker format="HH:mm" minuteStep={15} />
        </Form.Item>

        <Form.Item name="location" label="Backup Location" rules={[{ required: true, message: 'Please select a backup location' }]}>
          <Input.Group compact>
            <Form.Item name="location" noStyle rules={[{ required: true, message: 'Please select a backup location' }]}>
              <Input placeholder="e.g., C:\\Backups\\HelpDesk" />
            </Form.Item>
            <Button 
              icon={<FolderOpenOutlined />}
              onClick={() => onBrowseFolder('location', form)}
            >
              Browse
            </Button>
          </Input.Group>
        </Form.Item>

        <Form.Item name="retention_days" label="Retention Period (days)" rules={[{ required: true, message: 'Please enter retention days' }]}>
          <InputNumber min={1} max={365} />
        </Form.Item>

        <Form.Item name="enabled" label="Status" valuePropName="checked">
          <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Direct Restore Modal Component
const DirectRestoreModal = ({ open, onCancel, onRestore, uploadedFile, setUploadedFile, loading }) => {
  const handleFileUpload = (file, fileList) => {
    console.log('Selected file:', file); // Debug log to confirm selection (remove in production)
    if (file.type === 'application/sql' || file.name.endsWith('.sql')) {
      setUploadedFile(file);
      return false; // Prevent default upload behavior
    } else {
      message.error('Please upload a valid SQL file');
      return false;
    }
  };

  return (
    <Modal
      title="Direct Restore from File"
      open={open}
      onCancel={onCancel}
      onOk={onRestore}
      confirmLoading={loading}
      okButtonProps={{ disabled: !uploadedFile }}
      width={500}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          message="Warning"
          description="This operation will restore your database from the selected SQL file. This cannot be undone."
          type="warning"
          showIcon
        />
        
        <Upload
          accept=".sql"
          beforeUpload={handleFileUpload}
          showUploadList={false}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>
            Select SQL Backup File
          </Button>
        </Upload>
        
        {uploadedFile && (
          <Text>
            Selected file: <strong>{uploadedFile.name}</strong> ({formatFileSize(uploadedFile.size)})
          </Text>
        )}
      </Space>
    </Modal>
  );
};

// Helper functions
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const getStatusColor = (status) => {
  const colors = {
    completed: 'green',
    failed: 'red',
    running: 'blue',
    pending: 'orange',
    success: 'green',
    error: 'red'
  };
  return colors[status?.toLowerCase()] || 'default';
};

const getDayOfWeekName = (day) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[day] || day;
};

export default BackupRestoreManagement;